// web/src/utils/labels/ticket-outbound-notify.ts —— 工单来源 / 外呼失败与结果 / 说话人 / 渠道 / 通知 / AI 日志 / 通话阶段
// 拆自 web/src/utils/labels.ts 的同名分区（内容原样搬运）；由 web/src/utils/labels.ts 以 export * 聚合，导出面对调用方不变。

import { maskPhone } from '../format';
import type { PillColor } from '../../components/PillTag';

import { AI_STAGE_LABELS, ASSIST_SPEAKER_LABELS } from './assist-ai-intent';

/**
 * 建单来源与意图识别来源（ticket.source / 意图判定 source）→ 中文。
 * ai_intent 原在意图统计/测试台作「意图规则」、在工单列表与工单详情作「AI 意图」，
 * 此处统一取「意图规则」：与同表 ai_llm「LLM 判定」、ai_fallback「规则兜底」构成判定方式口径；
 * 工单页 AI 来源另有 AiBadge（电光青 + 机器人图标）表达 AI 语义，无需在文案里重复 AI 前缀。
 */
export const TICKET_SOURCE_LABELS: Record<string, string> = {
  manual: '人工',
  assist: '坐席一键',
  ai_intent: '意图规则',
  ai_llm: 'LLM 判定',
  ai_fallback: '规则兜底',
  ai_agent: '坐席通话',
  ops: '运营整改',
};

export function ticketSourceLabel(v?: string): string {
  if (!v) return '-';
  return TICKET_SOURCE_LABELS[v] || v;
}

/** 外呼失败原因（外呼明细 fail_reason）→ 中文 */
export const OUTBOUND_FAIL_REASON_LABELS: Record<string, string> = {
  no_answer: '无应答',
  busy: '占线',
  failed: '呼叫失败',
  blacklisted: '已拉黑',
  invalid: '无效号码',
  queue_timeout: '坐席超时',
  no_agent: '无坐席',
  esl_timeout: '事件丢失',
  daily_limit: '当日上限',
  ai_error: 'AI 会话异常',
  no_conversation: '无有效对话',
};

export function outboundFailReasonLabel(v?: string): string {
  if (!v) return '-';
  return OUTBOUND_FAIL_REASON_LABELS[v] || v;
}

/** 外呼结果确认状态（result_status）→ [文案, PillColor]（ai=AI 提取用 AI 语义青，与全站 AI 元素同色） */
export const OUTBOUND_RESULT_STATUS_LABELS: Record<string, [string, PillColor]> = {
  ai: ['AI 提取', 'cyan'],
  draft: ['待确认', 'gold'],
  submitted: ['已确认', 'green'],
};

export function outboundResultStatusMeta(v?: string): [string, PillColor] {
  return OUTBOUND_RESULT_STATUS_LABELS[v || ''] || [v || '-', 'gray'];
}

/** 外呼结果确认状态文案（纯文字场景用，不占胶囊） */
export function outboundResultStatusLabel(v?: string): string {
  return outboundResultStatusMeta(v)[0];
}

/* ---------- 通话 / 会话域 ---------- */

/**
 * 转写说话人（transcript.speaker）→ [文案, PillColor]。
 * 文案复用 ASSIST_SPEAKER_LABELS（客户/坐席/AI），此处只补标签色，不再另起文案表。
 */
const SPEAKER_COLORS: Record<string, PillColor> = {
  customer: 'arcoblue',
  agent: 'green',
  robot: 'cyan',
};

export function transcriptSpeakerMeta(v?: string): [string, PillColor] {
  const key = v || '';
  if (!key) return ['-', 'gray'];
  return [ASSIST_SPEAKER_LABELS[key] || key, SPEAKER_COLORS[key] || 'gray'];
}

/**
 * 在线会话渠道（chat_session.channel）→ [文案, PillColor]。
 * 取值以后端枚举为准（models.go：h5|wechat|miniprogram|api）：h5=网页访客端、wechat=公众号
 * （wechat-gateway 回调落库即 wechat）、miniprogram=小程序（chat 服务 code2session）、api=开放 API。
 * 三者不可互相顶替：把 wechat 显示成「小程序」、api 显示成「公众号」属展示口径错误。
 */
export const CHAT_CHANNEL_LABELS: Record<string, [string, PillColor]> = {
  h5: ['网页在线', 'arcoblue'],
  wechat: ['公众号', 'green'],
  miniprogram: ['小程序', 'cyan'],
  api: ['API', 'gray'],
};

export function chatChannelMeta(v?: string): [string, PillColor] {
  return CHAT_CHANNEL_LABELS[v || ''] || [v || '-', 'gray'];
}

/**
 * 通知类型（notification.type）→ 分类中文（铃铛分类展示）。
 *
 * 取值以上游 `notification.type` 的**实际写入点**为准（`server` 侧 `notify*.Create*` 调用，
 * 没有集中枚举表）；后端新增类型必须同批登记到这里——本函数是唯一渲染出口，
 * 漏登记的表现就是铃铛胶囊里直接露出英文标识（announcement / security / ai_alert 曾如此）。
 * 未知值仍按前端约定原样返回（不猜中文），故本表要跟住后端。
 */
export const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  risk_escalate: '风险上报',
  workflow_task: '流程待办',
  workflow: '流程通知',
  workflow_notify: '流程通知',
  workflow_done: '流程通知',
  workflow_rejected: '流程通知',
  ticket_urge: '工单催办',
  ticket_escalate: '工单提级',
  voc_ticket: 'VOC 整改',
  voc_alert: 'VOC 告警',
  cockpit_ticket: '驾驶舱整改',
  cockpit_alert: '驾驶舱告警',
  cockpit_incident: '根因沉淀',
  chat_new: '在线咨询',
  ob_no_agent: '外呼缺坐席',
  // 公告下发（monitor 广播公告同时落站内通知，铃铛可回看）
  announcement: '公告',
  // 账号安全：2FA 首绑 / 重置 / 强制解绑 / 恢复码登录（login-2fa.md）
  security: '账号安全',
  // AI 供应商余额/欠费类异常（ai_provider_alert → 通知全部启用管理员）
  ai_alert: 'AI 平台告警',
  // 排班上下班提醒（worker 排班循环，提醒式不强制）
  shift_start: '班次开始',
  shift_end: '班次结束',
};

export function notificationTypeLabel(v?: string): string {
  if (!v) return '-';
  return NOTIFICATION_TYPE_LABELS[v] || v;
}

/**
 * AI 调用日志阶段（ai 日志 stage）→ 中文。
 * 与 AI_STAGE_LABELS（实时通话 ai_stage.stage：asr/llm/tts/idle）不是同一枚举：
 * 本表是 AI 调用链路的细粒度阶段（会话/ASR/LLM/实时识别/TTS/变声/转接…）。
 */
export const AI_LOG_STAGE_LABELS: Record<string, string> = {
  session_start: '会话开始',
  // 本通实时 ASR 的接入留痕（机器人/坐席辅助各一行：平台 + 接入点，人工链路只此一处可查供应商）
  asr_start: 'ASR 接入',
  asr_sentence_end: 'ASR 识别返回',
  asr_drop: 'ASR 无效丢弃',
  // ASR 运行期留痕：语种判定、失败、停摆重建、结果纠正与同义词归一（供应商/错误原文见 detail）
  asr_lang: 'ASR 语种',
  asr_error: 'ASR 失败',
  asr_stall: 'ASR 停摆重建',
  asr_correct: 'ASR 结果纠正',
  asr_synonym_norm: 'ASR 同义词归一',
  barge_in: '抢话打断',
  // 抢话判定与处置：抑制/暂缓/追问/续播/重播/停止；未识别内容按消费处理
  barge_suppressed: '抢话抑制',
  barge_pause: '抢话暂缓',
  barge_ask: '抢话追问',
  barge_resume: '抢话续播',
  barge_replay: '抢话重播',
  barge_stop: '抢话停止',
  barge_unclear_consumed: '抢话未识别消费',
  llm_start: '主对话 LLM 发起',
  llm_return: '主对话 LLM 返回',
  llm_error: 'LLM 失败',
  session_intent: '实时识别 意图',
  // 实时识别链路健康：v2 判定与降级、LLM 失败、连续超时熔断
  intent_v2: '实时识别 意图（v2）',
  intent_v2_fallback: '实时识别 降级',
  realtime_llm_fail: '实时识别 LLM 失败',
  realtime_cooldown: '实时识别 熔断',
  realtime_transfer: '实时识别 转人工',
  intent_transfer: '实时识别 意图转人工',
  intent_collect: '实时识别 采集字段',
  intent_prompt: '实时识别 提示注入',
  intent_template: '实时识别 意图模板',
  // 意图识别 A/B 对照留痕（影子档位）：一行同时含 v1/v2 两条判定，是诊断数据不是识别结果
  intent_shadow: '意图对照（影子）',
  // 意图动作与守卫：由识别结果驱动的执行/拦截，不是判定本身（content 为意图码或动作名）
  intent_query: '意图技能调用',
  intent_query_guarded: '意图技能调用守卫',
  intent_close_low_conf: '低置信不执行收尾',
  intent_ignore: '意图忽略',
  intent_invalid_question: '无效问题解释',
  intent_fixed_script: '意图固定话术',
  intent_script_guarded: '意图固定话术守卫',
  intent_goodbye: '意图结束通话',
  intent_goodbye_guarded: '意图结束通话守卫',
  intent_notify: '意图通知',
  intent_collect_prompt: '意图采集追问',
  intent_collect_done: '意图采集完成',
  intent_collect_done_confirm: '意图采集完成确认',
  intent_collect_partial: '意图采集部分完成',
  intent_collect_dtmf: '意图采集按键',
  intent_ticket: '意图自动建单',
  intent_ticket_mark: '挂机建单标记',
  intent_ticket_no_template: '建单无模板',
  intent_ticket_canceled: '建单取消',
  intent_ticket_guarded: '建单守卫',
  intent_ticket_reentry_blocked: '建单重入阻断',
  intent_transfer_blocked: '意图转人工阻断',
  intent_transfer_guarded: '意图转人工守卫',
  intent_outbound_suppressed: '意图外呼抑制',
  intent_flow_guarded: '业务直达守卫',
  intent_repeat_allowed: '重复意图放行',
  intent_repeat_suppressed: '重复意图抑制',
  // 新口径：一轮播报 1 行 tts_turn + 每段 1 行 tts_segment
  tts_turn: 'TTS 播报轮次',
  tts_segment: 'TTS 播报分段',
  // 合成偏慢留痕（成功但耗时超阈值；含 wrote/first_byte 分段，用于区分云侧排队与建连问题）
  tts_slow: 'TTS 合成偏慢',
  tts_error: 'TTS 失败',
  tts_fail: 'TTS 播报失败',
  tts_fail_retry: 'TTS 播报重试',
  tts_budget_drop: 'TTS 超预算截断',
  tts_slow_on: '慢速播报开启',
  tts_slow_off: '慢速播报关闭',
  tts_slow_replay: '慢速复述',
  // 旧口径：库中历史通话的播报行是这 4 个 stage，映射勿删（否则历史记录只剩英文 stage）
  tts_build: 'TTS 引擎构建',
  tts_start: 'TTS 开始',
  tts_first_frame: 'TTS 首帧返回',
  tts_end: 'TTS 结束',
  vc_start: '变声连接',
  vc_end: '变声结束',
  vc_fallback: '变声降级直放',
  transfer_keyword: '转人工关键词',
  transfer_request: '转人工请求',
  transfer_direct: '直接转人工',
  transfer_deferred_no_agent: '无坐席延后转接',
  transfer_confirm_ask: '转人工确认征询',
  transfer_confirm_yes: '转人工确认同意',
  transfer_confirm_declined: '转人工确认拒绝',
  transfer_confirm_timeout: '转人工确认超时',
  transfer_confirm_escalated: '转人工确认升级',
  transfer_confirm_other: '转人工确认其它',
  hangup_keyword: '挂断关键词',
  hangup_request: '挂断请求',
  survey_request: '满意度请求',
  // 流程：进入/挂起交接/播报截断/语音拒收/数据源查询；flow_<type> 为按动作类型拼接（建单/通知/核验）
  flow_enter: '进入流程',
  flow_handoff: '流程挂起交接',
  flow_speak_cut: '流程播报截断',
  flow_voice_rejected: '流程语音拒收',
  flow_query_datasource: '流程数据源查询',
  flow_create_ticket: '流程建单',
  flow_notify: '流程通知',
  flow_verify: '流程核验',
  flow_dtmf_timeout: '流程按键超时',
  flow_dtmf_timeout_submit: '流程按键超时提交',
  flow_dtmf_reask: '流程按键重问',
  flow_dtmf_clear: '流程按键清空',
  flow_dtmf_empty_submit: '流程按键空提交',
  flow_dtmf_auto_submit: '流程按键自动提交',
  flow_dtmf_submit: '流程按键提交',
  // 按键采集（DTMF）
  dtmf_digit: '按键输入',
  dtmf_invalid: '按键无效',
  dtmf_timeout: '按键超时',
  dtmf_timeout_giveup: '按键超时放弃',
  dtmf_clear: '按键清空',
  // AI 并发准入与故障兜底
  ai_admit: 'AI 并发准入',
  ai_wait_timeout: 'AI 排队超时',
  ai_fallback: 'AI 故障兜底',
  fallback_action: '兜底动作',
  fallback_announcement: '兜底播报',
  // 会话与号码
  number_attr: '号码归属',
  privacy_number_attr: '隐私号码归属守卫',
  greeting_skip: '开场招呼免答',
  closing_hangup: '收尾挂断',
  hold: '等待音',
  backchannel: '冷场回应',
  silence_prompt: '静默追问',
  silence_goodbye: '静默告别',
  silence_reset: '静默计时重置',
  utterance_hold: '语句暂挂',
  utterance_merge: '语句合并',
  stage_strip: '话术标签剥离',
  // 提案与承诺度
  proposal_push: '提案推送',
  proposal_action: '提案动作',
  ai_commitment: '承诺提取',
  commitment_stale: '承诺失效',
  // 其他
  ack_consumed: '附和消费',
  announce_first: '开场先播',
  route_decision: '路由门禁判定',
};

export function aiLogStageLabel(v?: string): string {
  if (!v) return '-';
  return AI_LOG_STAGE_LABELS[v] || v;
}

/** 通话接待阶段（呼叫拓扑行）→ [文案, PillColor] */
export const CALL_STAGE_LABELS: Record<string, [string, PillColor]> = {
  ai: ['AI 接待中', 'purple'],
  transfer: ['AI → 人工', 'purple'],
  human: ['人工通话', 'arcoblue'],
  waiting: ['振铃/排队', 'orange'],
};

export function callStageMeta(v?: string): [string, PillColor] {
  return CALL_STAGE_LABELS[v || ''] || [v || '-', 'gray'];
}

/* ---------- 流程域 ---------- */

